import { useQuery } from "@apollo/client";
import NavbarLayout from "components/layouts/NavbarLayout";
import FundamenteiMetadata from "components/seo/FundamenteiMetadata";
import SitemapIssuerListingQuery from "lib/queries/SitemapIssuerListingQuery";
import type {
  ISitemapIssuerListingQuery,
  ISitemapIssuerListingQueryVariables,
} from "lib/queries/__generated__/SitemapIssuerListingQuery.generated";
import getQueryStringValue from "lib/utils/getQueryStringValue";
import type { NextPage } from "next";
import { IIssuerType } from "types/graphqlTypes";

interface ISitemapPageProps {
  filterBy: string | null;
  page: string | null;
}

const Sitemap: NextPage<ISitemapPageProps> = function Sitemap({ filterBy, page }: ISitemapPageProps) {
  const { data } = useQuery<ISitemapIssuerListingQuery, ISitemapIssuerListingQueryVariables>(
    SitemapIssuerListingQuery,
    {
      variables: {
        filterByIssuerType: filterBy as IIssuerType,
        skip: page ? (parseInt(page, 10) || 0) * 100 : 0,
        limit: 100,
      },
      skip: !filterBy,
    },
  );

  if (!data || !data.sitemapIssuerListing) {
    return null;
  }

  const pages = Math.ceil(data.sitemapIssuerListing.totalCount / 100);
  let pageTitle = "";
  let paginationTitle: string | null = null;

  if (filterBy === IIssuerType.AmericanCompany) {
    pageTitle = "Top Stocks";
    paginationTitle = "More Stocks";
  }

  if (filterBy === IIssuerType.AmericanReit) {
    pageTitle = "Top REITs";
    paginationTitle = "More REITs";
  }

  if (filterBy === IIssuerType.BrazilianCompany) {
    pageTitle = "Top Ações";
    paginationTitle = "Mais Ações";
  }

  if (filterBy === IIssuerType.BrazilianRealEstateFund) {
    pageTitle = "Top Fundos Imobiliários";
    paginationTitle = "Mais Fundos Imobiliários";
  }

  return (
    <NavbarLayout>
      <FundamenteiMetadata title={pageTitle} />

      <div className="mx-auto max-w-7xl px-4 font-aeonik font-normal xl:px-0">
        <div className="flex h-full w-full flex-col py-4">
          <h1 className="m-0 mt-8 text-xl font-bold text-gray-800 xl:text-3xl">{pageTitle}</h1>

          <div className="grid h-full w-full grid-cols-1 gap-4 gap-y-6 py-4 md:grid-cols-2 xl:grid-cols-3">
            {data.sitemapIssuerListing.nodes.map((issuer) => {
              const arrayOfStrings = issuer.url.split("/");
              const tickerSymbol = arrayOfStrings[arrayOfStrings.length - 1];

              return (
                <a key={`${issuer.name}/${tickerSymbol}`} href={issuer.url} className="text-gray-600 underline">
                  {issuer.name}
                </a>
              );
            })}
          </div>

          <h2 className="m-0 mt-8 text-lg font-bold text-gray-700 xl:text-2xl">{paginationTitle}</h2>
          <div className="grid h-full w-full grid-cols-[repeat(26,auto)] gap-4 py-4">
            {Array.from({ length: pages }).map((_, index) => (
              <a
                key={`page-${String(index)}`}
                href={`sitemap?filterBy=${filterBy}&page=${index}`}
                className="flex text-gray-600 underline"
              >
                {index + 1}
              </a>
            ))}
          </div>
        </div>
      </div>
    </NavbarLayout>
  );
};

export default Sitemap;

Sitemap.getInitialProps = async ({ query }) => ({
  filterBy: getQueryStringValue(query, "filterBy"),
  page: getQueryStringValue(query, "page"),
});
